import { useMemo } from 'react'
import { useGetAuthUserList } from '@/service-library/hooks/auth-user'
import { useGetOrganizations } from '@/service-library/hooks/organizations'
import { useGetPositions } from '@/service-library/hooks/positions'
import { toTagDisplay } from '@/utils/comment-tags'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'

type CommentTagOption = { id: string; display: string }

type UseCommentTagOptionsOptions = {
  enabled: boolean
}

export default function useCommentTagOptions({
  enabled,
}: UseCommentTagOptionsOptions) {
  const { document } = useDocumentContext()
  const projectId = document?.project_id
  const ownerOrgId = document?.owner_org_id

  const { users: authUsers } = useGetAuthUserList({
    filters: { fields__only: 'id,name,email', status__in: 'active' },
    enabled,
  })

  const { positions: allPositions } = useGetPositions({
    filters: { limit: '1000', project_id: projectId },
    enabled: enabled && !!projectId,
  })

  const { organizations } = useGetOrganizations({
    filters: {
      self_and_ancestors_for_id: ownerOrgId,
      fields__only: 'id',
      limit: '1000',
    },
    enabled: enabled && !!ownerOrgId,
  })

  const users = useMemo<CommentTagOption[]>(
    () =>
      authUsers.flatMap((user) =>
        user.id ? [{ id: user.id, display: toTagDisplay(user.name) }] : [],
      ),
    [authUsers],
  )

  const positions = useMemo<CommentTagOption[]>(() => {
    const allowedOrgIds = new Set(organizations.map((org) => org.id))
    return allPositions
      .filter(
        (position) =>
          position.dp_org_id === null || allowedOrgIds.has(position.dp_org_id),
      )
      .map((position) => ({
        id: position.id,
        display: toTagDisplay(position.name),
      }))
  }, [allPositions, organizations])

  return { users, positions }
}
